import { useCallback } from 'react';
import { usePostHog } from '@posthog/react';
import { buildUnitFocusEventProperties, UnitFocusEventContext } from '../businessLogicService/unitFocusAnalytics';

export type UnitFocusEventName =
  | 'Unit_focus_created'
  | 'unit_focus_moved'
  | 'unit_focus_end_initiated'
  | 'unit_focus_ended'
  | 'unit_focus_unitblocking_updated';

/** Properties specific to a single event, on top of the shared context. */
export type UnitFocusEventExtraProperties = Record<string, string | number | boolean>;

export type CaptureUnitFocusEvent = (
  name: UnitFocusEventName,
  context: UnitFocusEventContext,
  extraProperties?: UnitFocusEventExtraProperties,
) => void;

/**
 * The single capture point for Unit Focus analytics, so every event is guaranteed to
 * carry the class/unit/roadmap/blocking context instead of relying on each call site.
 */
export function useUnitFocusEvent(): CaptureUnitFocusEvent {
  const postHog = usePostHog();

  return useCallback<CaptureUnitFocusEvent>(
    (name, context, extraProperties) => {
      // Context last: an event's own properties must never overwrite it.

      postHog.capture(name, { ...extraProperties, ...buildUnitFocusEventProperties(context) });
    },
    [postHog],
  );
}
